//#region Angular Imports

import { IQService } from 'angular'

//#endregion

//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Model Imports

import { Enums } from '../../../models/common/enums'
import { IOpObject } from '../../../models/util/interfaces'
import { OpMeter, OpLocation } from '../../../models/core'

//#endregion

//#region Helper Imports

import { EventsNames } from '../../../helpers/events-names'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { LogHelper } from '../../../helpers/log-helper'

//#endregion

//#region Service Imports

import { MeterService } from '../../../services/meter'

//#endregion

import { BulkEditInitParams, CreateEditInitParams } from '../../common/create-edit-popup'
import { SourcePopover } from '../../common/select-box-popup'
import { CreateEditController, CreateEditScope } from '../../common/common.create-edit.controller'

import { MeterEditInitAdditionalParams } from './meters-create-edit.interfaces'

interface MeterCreateEditScope extends CreateEditScope {
    popupId: number
}

export class MeterCreateEditController extends CreateEditController {

    //#region Members

    private referenceValue: OpMeter
    private targetLocation: OpLocation

    //#endregion

    //#region Ctor
    
    public constructor(
        private $scope: MeterCreateEditScope,
        $timeout: ng.ITimeoutService,
        $q: IQService,
        private moduleName: Enums.SimaxModuleName,
        private meterService: MeterService,
    ) {
        super(MeterService.REFERENCE_TYPE, moduleName, $scope, $timeout, $q)

        this.$scope.popupId = new Date().getTime()
    }

    //#endregion

    //#region CreateEditController Overrides

    protected onInitialize(createEditInitParams: CreateEditInitParams<MeterEditInitAdditionalParams>): void {
        if (createEditInitParams.isCopy === true) {
            createEditInitParams.referenceObjectId = 0

            if (createEditInitParams.referenceObject != null) {
                createEditInitParams.referenceObject.idObject = 0
            }
        }

        this.referenceValue = createEditInitParams ? <OpMeter>createEditInitParams.referenceObject : null

        if (this.referenceValue && this.referenceValue.idObject > 0) {
            this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EDITING, this.moduleName)
        } else {
            this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__ADDING, this.moduleName)
        }

        if (createEditInitParams.additionalParameters != null) {
            this.targetLocation = createEditInitParams.additionalParameters.destinationLocation
        }

        super.openCreateEdit(createEditInitParams);
    }

    protected onBulkInitialize(bulkEditInitParams: BulkEditInitParams): void {
        this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__MASS_EDITING, this.moduleName)
        this.$scope.gridData = bulkEditInitParams;

        super.openBulkCreateEdit(bulkEditInitParams);
    }

    protected onSaving(objectToSave: IOpObject): JQueryPromise<IOpObject> {
        const meterToSave: OpMeter = OpMeter.fromIOpObject(objectToSave)
        const isNewMeter: boolean = meterToSave.idMeter == 0 && this.referenceValue == null
        const meterHasNoLocations: boolean = this.referenceValue != null && _.size(this.referenceValue.currentLocations) === 0

        if (isNewMeter || meterHasNoLocations) {
            if (this.targetLocation == null) {
                return this.openLocationSelect(meterToSave)
            }

            meterToSave.currentLocations = [this.targetLocation]
        } else {
            meterToSave.currentLocations = this.referenceValue.currentLocations
        }

        return this.saveMeter(meterToSave)
    }

    protected onBulkSaving(objectToSave: IOpObject): JQueryPromise<IOpObject> {
        return this.meterService.saveMeter(OpMeter.fromIOpObject(objectToSave), true)
    }

    //#endregion

    //#region Location Select

    private openLocationSelect(meter: OpMeter): JQueryPromise<OpMeter> {
        let isSaving: boolean = false
        const deferred: JQueryDeferred<OpMeter> = $.Deferred();

        const selectPopoverSource: SourcePopover = {
            popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectLocationToAssign),

            onSubmit: (idLocation: number) => {
                isSaving = true

                const opLocation: OpLocation = new OpLocation(null)

                opLocation.idLocation = idLocation
                meter.currentLocations = [opLocation]

                this.$scope.$root.$broadcast(EventsNames.SHOW_LOADING_INDICATOR)

                this.saveMeter(meter, deferred)
                    .always(() => {
                        isSaving = false
                    });
            },

            onHiding: () => {
                if (isSaving === false) [
                    deferred.reject()
                ]
            },

            selectPopoverSource: {
                dataTypeId: Enums.DataType.HELPER_ID_LOCATION,
                referenceTypeId: Enums.ReferenceType.IdLocation,
                selectedId: 0,
                dataIdSourceModule: Enums.WebSimaxModule.Locations,
            }
        }

        this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)
        this.$scope.$broadcast(EventsNames.OPEN_SELECT_POPOVER_POPUP + this.$scope.popupId, selectPopoverSource)

        return deferred.promise()
    }

    //#endregion

    //#region Save Meter

    private saveMeter(meterToSave: OpMeter, deferred: JQueryDeferred<OpMeter> = null): JQueryPromise<OpMeter> {
        deferred = deferred != null ? deferred : $.Deferred<OpMeter>()

        this.meterService
            .saveMeter(meterToSave)
            .then((savedObject: OpMeter) => {
                super.complete(savedObject);

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                super.completeWithError(error);

                LogHelper.error(MeterService.LOG_PREFIX, error)

                deferred.reject(error)
            })

        return deferred.promise();
    }

    //#endregion
}